import { clsx } from 'clsx'
import { CSSProperties, useMemo } from 'react'

const UNSELECTED = '$$UNSELECTED$$'

type PrimitiveValue = string | number

export type SelectProps<T, V extends PrimitiveValue> = {
  value?: V
  name?: string
  className?: string
  style?: CSSProperties
  placeholder?: string
  ariaLabel?: string
  dataTestId?: string
  disabled?: boolean
  onChange?: (value: V | undefined) => void
  options: T[]
  buildValue: (opt: T) => V
  buildLabel: (opt: T) => string | number
}

export function Select<T, V extends PrimitiveValue>({
  value,
  name,
  className,
  style,
  options,
  placeholder,
  ariaLabel,
  dataTestId,
  onChange,
  buildValue,
  buildLabel,
  disabled
}: SelectProps<T, V>) {
  const optionsComponents = useMemo(() => {
    return [
      ...(placeholder ? [<option key={''} value={UNSELECTED} label={placeholder} />] : []),
      ...options.map((opt, index) => {
        const optValue = buildValue(opt)
        const optLabel = buildLabel(opt)
        return (
          <option key={index} value={optValue}>
            {optLabel}
          </option>
        )
      })
    ]
  }, [options, placeholder, buildValue, buildLabel])

  const valueSelected = options.some((opt) => buildValue(opt) === value)

  return (
    <select
      name={name}
      className={clsx(
        'block rounded-md border-0 py-2 pl-3 pr-10 text-base text-gray-900 ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-gray-300',
        {
          'cursor-not-allowed opacity-50': disabled,
          'text-gray-500': !valueSelected
        },
        className
      )}
      style={style}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      disabled={disabled}
      value={value === undefined ? UNSELECTED : value}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
        onChange?.(e.target.value === UNSELECTED ? undefined : (e.target.value as V))
      }
    >
      {optionsComponents}
    </select>
  )
}
